import { LOCAL_COLORS } from 'styles/constants';
import {
  getReadableTextColor,
  hasReadableContrast,
  sanitizeHexColor,
} from 'utils/hexColor';

import type { ColorType } from './CtaButton';

/**
 * The background each preset variant paints on the button itself, so we can
 * check an editor's label color against what it will actually sit on. Keep in
 * sync with `variantStyles` in CtaButton.style.ts.
 */
const VARIANT_BACKGROUNDS: Record<Exclude<ColorType, 'custom'>, string> = {
  primary: LOCAL_COLORS.surfaceBrandBold,
  white: LOCAL_COLORS.surfaceWhite,
  'dark-blue': LOCAL_COLORS.surfaceDarkBold,
};

/**
 * The color the button ends up with, including the fall back to primary when a
 * custom color is missing or unusable.
 */
export const getCtaButtonBackground = (
  color: ColorType,
  sanitizedCustomColor?: string,
) =>
  color === 'custom'
    ? sanitizedCustomColor ?? LOCAL_COLORS.surfaceBrandBold
    : VARIANT_BACKGROUNDS[color] ?? LOCAL_COLORS.surfaceBrandBold;

/**
 * The label color the button ends up with: the editor's choice when they made
 * one, otherwise the automatic pick for a custom background.
 */
export const getCtaButtonTextColor = (
  color: ColorType,
  sanitizedCustomColor?: string,
  sanitizedTextColor?: string,
) =>
  sanitizedTextColor ??
  (color === 'custom' && sanitizedCustomColor
    ? getReadableTextColor(sanitizedCustomColor)
    : undefined);

/**
 * Returns a message describing a CTA color combination an editor can set in
 * Contentful but shouldn't, or `undefined` when the combination is fine.
 *
 * Kept as a pure function so it can be tested; the component only logs it in
 * development.
 */
export const getCtaButtonColorWarning = ({
  buttonText,
  color = 'primary',
  customColor,
  textColor,
}: {
  buttonText: string;
  color?: ColorType;
  customColor?: string;
  textColor?: string;
}) => {
  const sanitizedCustomColor = sanitizeHexColor(customColor);
  const sanitizedTextColor = sanitizeHexColor(textColor);
  const warnings: string[] = [];

  if (color === 'custom' && !sanitizedCustomColor) {
    warnings.push(
      `CtaButton "${buttonText}" has color "custom" but no valid Custom Color${
        customColor ? ` (got "${customColor}")` : ''
      }. Falling back to the primary color.`,
    );
  }

  // Check the colors the button actually ends up with, so this also covers the
  // automatic label pick, which only promises the better of navy and white and
  // can still fail AA on a mid-tone background.
  const background = getCtaButtonBackground(color, sanitizedCustomColor);
  const resolvedTextColor = getCtaButtonTextColor(
    color,
    sanitizedCustomColor,
    sanitizedTextColor,
  );

  if (
    resolvedTextColor &&
    !hasReadableContrast(background, resolvedTextColor)
  ) {
    warnings.push(
      sanitizedTextColor
        ? `CtaButton "${buttonText}" has low contrast: Button Text Color ${resolvedTextColor} on ${background} is below WCAG AA for 14px bold text.`
        : `CtaButton "${buttonText}" has low contrast: no label color reads well on ${background}, the closest is ${resolvedTextColor}. Pick a different Custom Color or set Button Text Color.`,
    );
  }

  return warnings.length > 0 ? warnings.join(' ') : undefined;
};
